import React, {
    useState,
    useEffect,
    useRef,
    KeyboardEvent,
    MouseEvent,
} from "react";
import { IoSearch, IoClose } from "react-icons/io5";
import "./SearchModal.scss";
import InputSearch from "../Inputs/InputSearch/InputSearch";
import { CiClock2, CiFolderOn, CiUser } from "react-icons/ci";
import { MdOutlineFactory } from "react-icons/md";
import { Item, ItemType, RecentItem } from "../../../types/types";
import { Link, useNavigate } from "react-router-dom";
import { convertirCamelCase } from "../../../utils/utils";

interface SearchDropdownEnhancedProps<T extends { id: string | number }> {
    placeholder?: string;
    className?: string;
    searchElements?: Record<string, T[]>;
    typeIconMap?: Record<string, (item: T) => React.ReactNode>;
    typeFieldsMap?: Record<string, (keyof T)[]>;
    typeRouteBaseMap?: Record<string, string>;
}

export function SearchModal<T extends { id: string | number }>({
    placeholder = "Buscar...",
    className = "",
    searchElements = {},
    typeIconMap = {},
    typeFieldsMap = {},
    typeRouteBaseMap = {},
}: SearchDropdownEnhancedProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
    const [filteredMap, setFilteredMap] = useState<Record<string, T[]>>({});

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            try {
                const stored = localStorage.getItem("recentItems");
                if (!stored) {
                    setRecentItems([]);
                } else {
                    const arr = JSON.parse(stored);
                    if (Array.isArray(arr)) {
                        setRecentItems(arr);
                    } else {
                        setRecentItems([]);
                    }
                }
            } catch (err) {
                console.error("Error cargando recientes:", err);
                setRecentItems([]);
            }
        }
    }, [isOpen]);

    const deepMatch = (obj: any, query: string): boolean => {
        const lower = query.toLowerCase();
        if (obj == null) return false;
        if (
            typeof obj === "string" ||
            typeof obj === "number" ||
            typeof obj === "boolean"
        ) {
            return String(obj).toLowerCase().includes(lower);
        }
        if (Array.isArray(obj)) {
            return obj.some((el) => deepMatch(el, query));
        }
        if (typeof obj === "object") {
            return Object.values(obj).some((val) => deepMatch(val, query));
        }
        return false;
    };

    useEffect(() => {
        if (searchQuery.trim() === "") {
            setFilteredMap(searchElements);
        } else {
            const newMap: Record<string, T[]> = {};
            for (const [type, list] of Object.entries(searchElements)) {
                const filtered = list.filter((item) =>
                    deepMatch(item, searchQuery)
                );
                if (filtered.length > 0) {
                    newMap[type] = filtered;
                }
            }
            setFilteredMap(newMap);
        }
    }, [searchQuery, searchElements]);

    // Cerrar con Esc
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closeDropdown();
            }
        };
        window.addEventListener("keydown", handleKeyDown as any);
        return () => {
            window.removeEventListener("keydown", handleKeyDown as any);
        };
    }, [isOpen]);

    const handleClickOutside = (e: MouseEvent) => {
        if (
            isOpen &&
            containerRef.current &&
            !containerRef.current.contains(e.target as Node)
        ) {
            closeDropdown();
        }
    };
    useEffect(() => {
        if (isOpen) {
            window.addEventListener("mousedown", handleClickOutside as any);
            return () => {
                window.removeEventListener(
                    "mousedown",
                    handleClickOutside as any
                );
            };
        }
    }, [isOpen]);

    const openDropdown = () => {
        setIsOpen(true);
    };
    const closeDropdown = () => {
        setIsOpen(false);
    };

    const onInputFocus = () => {
        openDropdown();
    };

    const onInputChange = (e: string) => {
        setSearchQuery(e);
    };

    const onClear = () => {
        setSearchQuery("");
    };

    const handleItemClick = (type: string, item: T) => {
        const base = typeRouteBaseMap[type];
        console.log("Navigating to:", base, "with item:", item);
        setIsOpen(false);
    };

    return (
        <div
            className={`search-dropdown-enhanced ${className}`}
            ref={containerRef}
        >
            <InputSearch
                placeholder={placeholder}
                onSearch={onInputChange}
                onFocus={onInputFocus}
            />

            {isOpen && (
                <div className="search-dropdown-enhanced__panel">
                    {searchQuery.trim() !== "" &&
                    Object.keys(filteredMap).length === 0 ? (
                        <div className="search-dropdown-enhanced__empty">
                            <span>No se encontraron resultados.</span>
                        </div>
                    ) : (
                        <div className="search-dropdown-enhanced__sections">
                            {searchQuery.trim() === "" ? (
                                <>
                                    {recentItems.length === 0 ? (
                                        <div className="search-dropdown-enhanced__empty">
                                            <span>
                                                No hay elementos recientes.
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="search-dropdown-enhanced__section">
                                            <div className="search-dropdown-enhanced__section-title">
                                                Recientes
                                            </div>
                                            <ul className="search-dropdown-enhanced__list">
                                                {recentItems.map((rec) => {
                                                    const type =
                                                        rec.type as ItemType;
                                                    const base =
                                                        typeRouteBaseMap[
                                                            type
                                                        ] || "/";
                                                    const keyProp = "id";
                                                    const href = `${base}/${rec[keyProp]}`;
                                                    const fields =
                                                        typeFieldsMap[type] ||
                                                        ([
                                                            "title",
                                                        ] as (keyof T)[]);
                                                    const text = fields
                                                        .map((f) => {
                                                            // En recentItems solo conocemos "title", pero si el campo f no existe, el String será “undefined”
                                                            // Puedes ajustar para que use rec.title si f no está presente
                                                            const val = (
                                                                rec as any
                                                            )[f];
                                                            return val != null
                                                                ? String(val)
                                                                : "";
                                                        })
                                                        .filter((s) => s !== "")
                                                        .join(" • ");
                                                    return (
                                                        <li
                                                            key={rec.id}
                                                            className="search-dropdown-enhanced__item"
                                                        >
                                                            <Link
                                                                to={href}
                                                                className="search-dropdown-enhanced__item-link"
                                                            >
                                                                <span className="search-dropdown-enhanced__icon">
                                                                    {typeIconMap[
                                                                        type as ItemType
                                                                    ]?.(
                                                                        rec as unknown as T
                                                                    )}
                                                                </span>
                                                                <span className="search-dropdown-enhanced__item-title">
                                                                    {text ||
                                                                        rec.title}
                                                                </span>
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            ) : (
                                (
                                    Object.entries(filteredMap) as [
                                        ItemType,
                                        T[]
                                    ][]
                                ).map(([type, list]) => (
                                    <div
                                        key={type}
                                        className="search-dropdown-enhanced__section"
                                    >
                                        <div className="search-dropdown-enhanced__section-title">
                                            {convertirCamelCase(type)}
                                        </div>
                                        <ul className="search-dropdown-enhanced__list">
                                            {list.map((item) => {
                                                const fields =
                                                    typeFieldsMap[type] || [];
                                                const base =
                                                    typeRouteBaseMap[type] ||
                                                    "/";
                                                const keyField =
                                                    fields.length > 0
                                                        ? fields[0]
                                                        : "id";
                                                const href = `${base}/${
                                                    (item as any)[keyField]
                                                }`;
                                                const text =
                                                    fields.length > 0
                                                        ? fields
                                                              .map((f) => {
                                                                  const val = (
                                                                      item as any
                                                                  )[f];
                                                                  return val !=
                                                                      null
                                                                      ? String(
                                                                            val
                                                                        )
                                                                      : "";
                                                              })
                                                              .filter(
                                                                  (s) =>
                                                                      s !== ""
                                                              )
                                                              .join(" - ")
                                                        : String(
                                                              (item as any).id
                                                          );
                                                return (
                                                    <li
                                                        key={item.id}
                                                        className="search-dropdown-enhanced__item"
                                                    >
                                                        <Link
                                                            to={href}
                                                            className="search-dropdown-enhanced__item-link"
                                                        >
                                                            <span className="search-dropdown-enhanced__icon">
                                                                {typeIconMap[
                                                                    type as ItemType
                                                                ]?.(item)}
                                                            </span>
                                                            <span className="search-dropdown-enhanced__item-title">
                                                                {String(text)}
                                                            </span>
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
